if (!customElements.get('routine-builder')) {
  class RoutineBuilder extends HTMLElement {
    connectedCallback() {
      this.selection = {};
      this.addButton = this.querySelector('[data-add]');
      this.countEl = this.querySelector('[data-count]');
      this.totalEl = this.querySelector('[data-total]');
      this.errorEl = this.querySelector('[data-error-message]');

      this.querySelectorAll('[data-slot-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.togglePanel(button.closest('[data-step]').dataset.step));
      });

      this.querySelectorAll('[data-slot-remove]').forEach((button) => {
        button.addEventListener('click', () => this.clearStep(button.closest('[data-step]').dataset.step));
      });

      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.querySelectorAll('[data-choice]').forEach((choice) => this.bindChoice(panel, choice));
        panel.querySelector('[data-shade-back]')?.addEventListener('click', () => this.closeShades(panel, true));
        panel.querySelector('[data-panel-done]')?.addEventListener('click', () => {
          this.closePanels();
          const slotButton = this.slotButton(panel.dataset.panel);
          slotButton?.focus({ preventScroll: true });
          slotButton?.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'center' });
        });
      });

      // La cliente choisit d'abord les sous-catégories : seules celles-ci apparaissent en cases.
      this.querySelectorAll('[data-pick]').forEach((pick) => {
        pick.addEventListener('click', () => this.togglePick(pick));
      });

      this.querySelectorAll('[data-group-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggleGroup(button.closest('[data-group]')));
      });

      this.review = this.querySelector('[data-review]');
      this.addButton?.addEventListener('click', () => this.openReview());
      this.review?.querySelector('[data-review-back]').addEventListener('click', () => this.closeReview());
      this.review?.querySelector('[data-review-confirm]').addEventListener('click', () => this.addToCart());
      this.review?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeReview();
      });
      window.addEventListener('popstate', () => this.closeReview(true));

      // La trousse reste enregistrée sur l'appareil de la cliente jusqu'à ce qu'elle la vide.
      this.storageKey = `moons-trousse:${window.location.pathname}`;
      this.clearButton = this.querySelector('[data-clear]');
      this.clearButton?.addEventListener('click', () => this.handleClear());

      // Les grandes collections chargent la suite de leurs produits dans l'onglet, sans quitter la page.
      this.update();
      this.loadMore().finally(() => this.restore());
    }

    bindChoice(panel, choice) {
      choice.addEventListener('click', () => {
        if (choice.hasAttribute('data-multi')) {
          this.openShades(panel.dataset.panel, choice);
        } else {
          this.choose(panel.dataset.panel, choice);
        }
      });
    }

    async loadMore() {
      const lists = [...this.querySelectorAll('[data-more-url]')];
      await Promise.all(
        lists.map(async (list) => {
          const panel = list.closest('[data-panel]');
          let url = list.dataset.moreUrl;
          let pages = 0;
          while (url && pages < 10) {
            pages += 1;
            try {
              const response = await fetch(url);
              if (!response.ok) break;
              const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
              const more = doc.querySelector('[data-routine-more]');
              if (!more) break;
              more.querySelectorAll(':scope > .routine__choice').forEach((item) => {
                const node = document.importNode(item, true);
                list.appendChild(node);
                node.querySelectorAll('[data-choice]').forEach((choice) => this.bindChoice(panel, choice));
              });
              url = more.dataset.nextUrl;
            } catch (error) {
              break;
            }
          }
        })
      );
    }

    save() {
      if (!this.storageKey || this.restoring) return;
      try {
        if (Object.keys(this.selection).length) {
          localStorage.setItem(this.storageKey, JSON.stringify(this.selection));
        } else {
          localStorage.removeItem(this.storageKey);
        }
      } catch (error) {}
    }

    restore() {
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      } catch (error) {}
      if (!saved || typeof saved !== 'object') {
        this.update();
        return;
      }

      this.restoring = true;
      Object.entries(saved).forEach(([key, item]) => {
        const stepId = (item && item.stepId) || key.split('|')[0];
        const slot = this.querySelector(`[data-step="${stepId}"]`);
        const panel = this.querySelector(`[data-panel="${stepId}"]`);
        if (!slot || !panel || !item || !item.id) return;

        // Produit à référence unique
        let choice = panel.querySelector(`[data-choice][data-variant-id="${item.id}"]`);
        let productButton = null;

        // Produit à plusieurs références : on retrouve la teinte choisie
        if (!choice) {
          panel.querySelectorAll('.routine__choice').forEach((entry) => {
            if (choice) return;
            const template = entry.querySelector('template[data-shades]');
            const shade = template?.content.querySelector(`[data-shade][data-variant-id="${item.id}"]`);
            if (shade) {
              choice = shade;
              productButton = entry.querySelector('[data-choice]');
            }
          });
        }
        if (!choice) return; // produit plus disponible

        slot.hidden = false;
        const pick = this.querySelector(`[data-pick="${stepId}"]`);
        if (pick) pick.setAttribute('aria-pressed', 'true');
        this.choose(stepId, choice, { silent: true, keep: true });
      });
      this.restoring = false;
      this.update();
    }

    handleClear() {
      const button = this.clearButton;
      if (button.dataset.confirming !== 'true') {
        button.dataset.confirming = 'true';
        button.textContent = button.dataset.confirmLabel;
        clearTimeout(this.clearTimer);
        this.clearTimer = setTimeout(() => {
          button.dataset.confirming = 'false';
          button.textContent = button.dataset.label;
        }, 4000);
        return;
      }
      clearTimeout(this.clearTimer);
      button.dataset.confirming = 'false';
      button.textContent = button.dataset.label;

      this.clearAll();
      this.querySelectorAll('[data-pick]').forEach((pick) => {
        pick.setAttribute('aria-pressed', 'false');
        const slot = this.querySelector(`[data-step="${pick.dataset.pick}"]`);
        if (slot) slot.hidden = true;
      });
      this.closePanels();
      this.update();
    }

    toggleGroup(group) {
      const toggle = group.querySelector('[data-group-toggle]');
      const body = group.querySelector('.routine__group-body');
      const willOpen = body.hidden;
      body.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
      if (!willOpen) {
        group.querySelectorAll('[data-panel]').forEach((panel) => (panel.hidden = true));
        group.querySelectorAll('[data-slot-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        return;
      }
      // Case simple (ex. Accessoires) : on ouvre directement le choix des produits.
      if (group.dataset.groupSize === '1') {
        const step = group.querySelector('[data-step]');
        const panel = step && this.querySelector(`[data-panel="${step.dataset.step}"]`);
        if (panel && panel.hidden && !this.hasStep(step.dataset.step)) this.togglePanel(step.dataset.step);
      }
    }

    togglePick(pick) {
      const stepId = pick.dataset.pick;
      const slot = this.querySelector(`[data-step="${stepId}"]`);
      const willAdd = pick.getAttribute('aria-pressed') !== 'true';
      pick.setAttribute('aria-pressed', String(willAdd));

      if (willAdd) {
        slot.hidden = false;
        this.togglePanel(stepId);
        return;
      }

      const panel = this.querySelector(`[data-panel="${stepId}"]`);
      if (panel && !panel.hidden) this.closePanels();
      if (this.hasStep(stepId)) this.clearStep(stepId, { silent: true });
      slot.hidden = true;
      this.update();
    }

    togglePanel(stepId) {
      const panel = this.querySelector(`[data-panel="${stepId}"]`);
      const willOpen = panel.hidden;
      this.closePanels();
      if (!willOpen) return;
      panel.hidden = false;
      this.slotButton(stepId).setAttribute('aria-expanded', 'true');
      panel.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
    }

    // Produit à plusieurs références : la cliente choisit obligatoirement sa teinte, rien n'est pré-sélectionné.
    openShades(stepId, productButton) {
      const panel = this.querySelector(`[data-panel="${stepId}"]`);
      const view = panel.querySelector('[data-shade-view]');
      const list = view.querySelector('[data-shade-list]');
      const template = productButton.closest('.routine__choice').querySelector('template[data-shades]');
      const chosenIds = new Set(this.stepItems(stepId).map((item) => item.id));

      list.innerHTML = '';
      list.appendChild(template.content.cloneNode(true));
      view.querySelector('[data-shade-product]').textContent = productButton.dataset.title;

      list.querySelectorAll('[data-shade]').forEach((shade) => {
        if (chosenIds.has(Number(shade.dataset.variantId))) shade.setAttribute('aria-pressed', 'true');
        shade.addEventListener('click', () => {
          this.choose(stepId, shade);
          shade.setAttribute('aria-pressed', String(Boolean(this.selection[`${stepId}|${shade.dataset.variantId}`])));
        });
      });

      panel.querySelector('.routine__choices').hidden = true;
      view.hidden = false;
      view.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
      list.querySelector('[data-shade]')?.focus({ preventScroll: true });
    }

    closeShades(panel, focusGrid = false) {
      const view = panel.querySelector('[data-shade-view]');
      if (!view) return;
      view.hidden = true;
      view.querySelector('[data-shade-list]').innerHTML = '';
      const grid = panel.querySelector('.routine__choices');
      grid.hidden = false;
      if (focusGrid) grid.querySelector('[data-choice]')?.focus({ preventScroll: true });
    }

    closePanels() {
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = true;
        this.closeShades(panel);
      });
      this.querySelectorAll('[data-slot-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    }

    slotButton(stepId) {
      return this.querySelector(`[data-step="${stepId}"] [data-slot-toggle]`);
    }

    // Plusieurs produits possibles par case : on ajoute ou on retire le produit touché.
    stepItems(stepId) {
      return Object.values(this.selection).filter((item) => item.stepId === stepId);
    }

    hasStep(stepId) {
      return this.stepItems(stepId).length > 0;
    }

    clearAll() {
      const steps = new Set(Object.values(this.selection).map((item) => item.stepId));
      steps.forEach((stepId) => this.clearStep(stepId, { silent: true }));
    }

    choose(stepId, choice, options = {}) {
      const id = Number(choice.dataset.variantId);
      const key = `${stepId}|${id}`;
      if (this.selection[key] && !options.keep) {
        delete this.selection[key];
      } else {
        this.selection[key] = {
          stepId,
          id,
          price: Number(choice.dataset.price),
          image: choice.dataset.image,
          title: choice.dataset.title,
        };
      }
      this.refreshStep(stepId);
      if (options.silent) return;
      this.update();
    }

    refreshStep(stepId) {
      const items = this.stepItems(stepId);
      const slot = this.querySelector(`[data-step="${stepId}"]`);
      const panel = this.querySelector(`[data-panel="${stepId}"]`);
      if (!slot) return;

      slot.classList.toggle('is-filled', items.length > 0);
      const name = slot.querySelector('[data-slot-name]');
      if (items.length === 0) {
        name.textContent = slot.dataset.emptyLabel || '';
      } else if (items.length === 1) {
        name.textContent = items[0].title || '';
      } else {
        name.textContent = `${items.length} ${this.dataset.productsLabel || 'produits'}`;
      }
      const total = items.reduce((sum, item) => sum + item.price, 0);
      slot.querySelector('[data-slot-price]').textContent = items.length ? this.formatMoney(total) : '';

      const media = slot.querySelector('[data-slot-media]');
      media.innerHTML = '';
      media.classList.toggle('is-stack', items.length > 1);
      if (!items.length) {
        media.innerHTML = '<span class="routine__slot-plus" aria-hidden="true">+</span>';
      } else {
        items.slice(0, 3).forEach((item) => {
          if (!item.image) return;
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        });
      }
      slot.querySelector('[data-slot-remove]').hidden = items.length === 0;

      if (!panel) return;
      const ids = new Set(items.map((item) => item.id));
      panel.querySelectorAll('.routine__choice').forEach((entry) => {
        const button = entry.querySelector('[data-choice]');
        let variantIds = [];
        if (button.dataset.variantId) {
          variantIds = [Number(button.dataset.variantId)];
        } else {
          const template = entry.querySelector('template[data-shades]');
          variantIds = [...(template?.content.querySelectorAll('[data-shade]') || [])].map((shade) =>
            Number(shade.dataset.variantId)
          );
        }
        const chosen = variantIds.filter((variantId) => ids.has(variantId)).length;
        button.setAttribute('aria-pressed', String(chosen > 0));
        let badge = button.querySelector('.routine__choice-check');
        if (chosen > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'routine__choice-check';
            badge.setAttribute('aria-hidden', 'true');
            button.appendChild(badge);
          }
          badge.textContent = chosen > 1 ? `✓ ${chosen}` : '✓';
        } else if (badge) {
          badge.remove();
        }
      });
    }

    // Odeur préférée : filtre les produits de chaque étape et pré-remplit une routine conseillée.
    // Odeur (famille) : un produit conseillé par étape.
    // Gamme : tous les produits disponibles de la gamme, à garder ou retirer.
    applyScent(scentId) {
      this.scent = scentId;
      const chip = scentId ? this.querySelector(`[data-scent="${scentId}"]`) : null;
      const isRange = chip?.dataset.scentKind === 'gamme';
      this.querySelectorAll('[data-scent]').forEach((entry) =>
        entry.setAttribute('aria-pressed', String(entry.dataset.scent === scentId))
      );

      this.querySelectorAll('[data-panel]').forEach((panel) => {
        let visible = 0;
        panel.querySelectorAll('.routine__choice').forEach((item) => {
          const scents = (item.dataset.scents || '').split(' ');
          const match = !scentId || scents.includes(scentId);
          item.hidden = !match;
          if (match) visible += 1;
        });
        const empty = panel.querySelector('[data-scent-empty]');
        if (empty) empty.hidden = visible > 0;
      });

      this.clearAll();
      this.querySelectorAll('[data-pick]').forEach((pick) => {
        pick.setAttribute('aria-pressed', 'false');
        const slot = this.querySelector(`[data-step="${pick.dataset.pick}"]`);
        if (slot) slot.hidden = true;
      });

      if (scentId) {
        this.querySelectorAll('[data-panel]').forEach((panel) => {
          const stepId = panel.dataset.panel;
          // Jamais de référence imposée : on ne pré-remplit qu'avec des produits à référence unique.
          const candidates = [...panel.querySelectorAll('.routine__choice:not([hidden]) [data-choice]:not([data-multi])')];
          // Un même produit présent dans deux étapes n'est ajouté qu'une fois.
          const taken = new Set(Object.values(this.selection).map((item) => item.id));
          const fresh = candidates.filter((choice) => !taken.has(Number(choice.dataset.variantId)));
          const chosen = isRange ? fresh : fresh.slice(0, 1);
          chosen.forEach((choice) => this.choose(stepId, choice, { silent: true, keep: true }));

          // Les étapes avec des produits de cette odeur ou gamme restent visibles.
          const hasMatch = panel.querySelector('.routine__choice:not([hidden])');
          if (hasMatch) {
            const slot = this.querySelector(`[data-step="${stepId}"]`);
            const pick = this.querySelector(`[data-pick="${stepId}"]`);
            if (slot) slot.hidden = false;
            if (pick) pick.setAttribute('aria-pressed', 'true');
          }
        });
      }
      this.closePanels();
      this.update();

      this.querySelectorAll('[data-scent-note]').forEach((note) => {
        note.hidden = !scentId || (note.dataset.scentNote === 'gamme') !== isRange;
      });
      const reset = this.querySelector('[data-scent-reset]');
      if (reset) reset.hidden = !scentId;
    }

    clearStep(stepId, options = {}) {
      Object.keys(this.selection).forEach((key) => {
        if (this.selection[key].stepId === stepId) delete this.selection[key];
      });
      this.refreshStep(stepId);
      if (options.silent) return;
      this.slotButton(stepId).focus();
      this.update();
    }

    update() {
      const items = Object.values(this.selection);
      const total = items.reduce((sum, item) => sum + item.price, 0);
      this.countEl.textContent = items.length;
      this.totalEl.textContent = this.formatMoney(total);
      this.addButton.disabled = items.length === 0;
      if (this.clearButton) this.clearButton.hidden = items.length === 0;
      this.save();

      this.querySelectorAll('[data-group]').forEach((group) => {
        if (!group.matches('.routine__group')) return;
        const groupSteps = new Set([...group.querySelectorAll('[data-step]')].map((step) => step.dataset.step));
        const picks = items.filter((item) => groupSteps.has(item.stepId));
        group.classList.toggle('has-picks', picks.length > 0);
        group.querySelector('[data-group-count]').textContent = picks.length;
        const thumbs = group.querySelector('[data-group-thumbs]');
        thumbs.innerHTML = '';
        picks
          .filter((pick) => pick.image)
          .slice(0, 4)
          .forEach((pick) => {
            const img = document.createElement('img');
            img.src = pick.image;
            img.alt = '';
            thumbs.appendChild(img);
          });
      });
      this.errorEl.hidden = true;
    }

    // Récapitulatif : une vraie trousse avec les produits choisis, avant de valider.
    openReview() {
      if (!Object.keys(this.selection).length) return;
      this.renderReview();
      this.review.hidden = false;
      document.body.classList.add('routine-review-open');
      this.reviewOpenedWithHistory = false;
      try {
        history.pushState({ routineReview: true }, '');
        this.reviewOpenedWithHistory = true;
      } catch (error) {}
      window.scrollTo({ top: 0 });
      this.review.scrollTop = 0;
      this.review.querySelector('.routine__review-heading').focus({ preventScroll: true });
    }

    closeReview(fromHistory = false) {
      if (!this.review || this.review.hidden) return;
      this.review.hidden = true;
      document.body.classList.remove('routine-review-open');
      if (!fromHistory && this.reviewOpenedWithHistory) {
        this.reviewOpenedWithHistory = false;
        history.back();
      }
      this.reviewOpenedWithHistory = false;
      this.addButton?.focus({ preventScroll: true });
      this.addButton?.scrollIntoView({ block: 'center' });
    }

    renderReview() {
      const pouch = this.review.querySelector('[data-review-pouch]');
      const list = this.review.querySelector('[data-review-list]');
      pouch.innerHTML = '';
      list.innerHTML = '';
      const entries = Object.entries(this.selection);

      entries.forEach(([key, item], index) => {
        const stepId = item.stepId;
        const slot = this.querySelector(`[data-step="${stepId}"]`);
        const stepLabel = slot?.querySelector('.routine__slot-label')?.textContent.trim() || '';

        const pouchItem = document.createElement('li');
        pouchItem.className = 'routine__pouch-item';
        pouchItem.style.setProperty('--tilt', `${[-6, 4, -3, 7, -5, 3, -7, 5][index % 8]}deg`);
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          pouchItem.appendChild(img);
        }
        pouch.appendChild(pouchItem);

        const row = document.createElement('li');
        row.className = 'routine__review-row';
        const media = document.createElement('span');
        media.className = 'routine__review-media';
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }
        const info = document.createElement('span');
        info.className = 'routine__review-info';
        const step = document.createElement('span');
        step.className = 'routine__review-step';
        step.textContent = stepLabel;
        const name = document.createElement('span');
        name.className = 'routine__review-name';
        name.textContent = item.title || '';
        const price = document.createElement('span');
        price.className = 'routine__review-price';
        price.textContent = this.formatMoney(item.price);
        info.append(step, name, price);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'routine__review-remove';
        remove.textContent = this.dataset.reviewRemove || 'Retirer';
        remove.addEventListener('click', () => {
          delete this.selection[key];
          this.refreshStep(stepId);
          this.update();
          if (!Object.keys(this.selection).length) {
            this.closeReview();
            return;
          }
          this.renderReview();
        });
        row.append(media, info, remove);
        list.appendChild(row);
      });

      const total = entries.reduce((sum, [, item]) => sum + item.price, 0);
      this.review.querySelector('[data-review-count]').textContent = entries.length;
      this.review.querySelector('[data-review-total]').textContent = this.formatMoney(total);
      this.review.classList.toggle('is-few', entries.length <= 2);
    }

    async addToCart() {
      const items = Object.values(this.selection).map((item) => ({ id: item.id, quantity: 1 }));
      if (!items.length) return;
      const button = this.review.querySelector('[data-review-confirm]');
      const errorEl = this.review.querySelector('[data-review-error]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      errorEl.hidden = true;
      try {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error('add failed');
        window.location.href = this.dataset.cartUrl;
      } catch (error) {
        errorEl.textContent = this.dataset.error;
        errorEl.hidden = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '{{amount}}';
      const withDelimiters = (number, precision, thousands, decimal) => {
        const fixed = (number / 100).toFixed(precision);
        const [whole, fraction] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return withDelimiters(cents, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(cents, 0, '.', ',');
          case 'amount_with_space_separator':
            return withDelimiters(cents, 2, ' ', ',');
          default:
            return withDelimiters(cents, 2, ',', '.');
        }
      });
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('routine-builder', RoutineBuilder);
}
